import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { contractsApi } from '@/services/contracts'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import type {
  ContractListQuery,
  ContractStatusUpdateRequest,
  CreateContractRequest,
  EditContractRequest,
  RenewContractRequest,
  ListQuery,
} from '@/types'

export const CONTRACT_DETAIL_TAB_STALE_TIME = 5 * 60_000
export const CONTRACT_DETAIL_INTERVENTIONS_QUERY = {
  page: 1,
  limit: 20,
  search: undefined,
  sort_column: 'scheduled_date',
  sort_order: 'desc',
} satisfies ListQuery
export const CONTRACT_DETAIL_DOCUMENTS_QUERY = {
  limit: 100,
} satisfies ListQuery

export function useContractsList(q: ContractListQuery) {
  return useQuery({
    queryKey: ['contracts', 'list', q],
    queryFn: () => contractsApi.list(q),
  })
}

export function useContractSummary() {
  return useQuery({
    queryKey: ['contracts', 'summary'],
    queryFn: () => contractsApi.summary(),
  })
}

export function useContract(id: string | undefined) {
  return useQuery({
    queryKey: ['contracts', 'detail', id],
    queryFn: () => contractsApi.get(id as string),
    enabled: !!id,
  })
}

export function contractHistoryQueryOptions(
  id: string,
  q: ListQuery,
  staleTime = 0
) {
  return queryOptions({
    queryKey: ['contracts', 'history', id, q],
    queryFn: () => contractsApi.history(id, q),
    staleTime,
  })
}

export function useContractHistory(
  id: string | undefined,
  q: ListQuery,
  options?: { enabled?: boolean; staleTime?: number }
) {
  return useQuery({
    ...contractHistoryQueryOptions(id as string, q, options?.staleTime),
    enabled: !!id && (options?.enabled ?? true),
  })
}

export function contractInterventionsQueryOptions(
  id: string,
  q: ListQuery,
  staleTime = 0
) {
  return queryOptions({
    queryKey: ['contracts', 'interventions', id, q],
    queryFn: () => contractsApi.interventions(id, q),
    staleTime,
  })
}

export function useContractInterventions(
  id: string | undefined,
  q: ListQuery,
  options?: { enabled?: boolean; staleTime?: number }
) {
  return useQuery({
    ...contractInterventionsQueryOptions(id as string, q, options?.staleTime),
    enabled: !!id && (options?.enabled ?? true),
  })
}

export function contractDocumentsQueryOptions(
  id: string,
  q: ListQuery,
  staleTime = 0
) {
  return queryOptions({
    queryKey: ['contracts', 'documents', id, q],
    queryFn: () => contractsApi.documents(id, q),
    staleTime,
  })
}

export function useContractDocuments(
  id: string | undefined,
  q: ListQuery,
  options?: { enabled?: boolean; staleTime?: number }
) {
  return useQuery({
    ...contractDocumentsQueryOptions(id as string, q, options?.staleTime),
    enabled: !!id && (options?.enabled ?? true),
  })
}

export function useCreateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateContractRequest) => contractsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts', 'list'] })
      qc.invalidateQueries({ queryKey: ['contracts', 'summary'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useUpdateContract(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditContractRequest) => contractsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts', 'list'] })
      qc.invalidateQueries({ queryKey: ['contracts', 'summary'] })
      qc.invalidateQueries({ queryKey: ['contracts', 'detail', id] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useRenewContract(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RenewContractRequest) => contractsApi.renew(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts', 'list'] })
      qc.invalidateQueries({ queryKey: ['contracts', 'summary'] })
      qc.invalidateQueries({ queryKey: ['contracts', 'detail', id] })
      qc.invalidateQueries({ queryKey: ['contracts', 'history', id] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useUpdateContractStatus(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ContractStatusUpdateRequest) =>
      contractsApi.updateStatus(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts', 'list'] })
      qc.invalidateQueries({ queryKey: ['contracts', 'summary'] })
      qc.invalidateQueries({ queryKey: ['contracts', 'detail', id] })
      qc.invalidateQueries({ queryKey: ['contracts', 'history', id] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useDeleteContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => contractsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts', 'list'] })
      qc.invalidateQueries({ queryKey: ['contracts', 'summary'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}
